/*
  <smx-carousel> for the SMX home sections.
  Markup: a [data-smx-track] scroller (CSS scroll-snap) plus optional
  [data-smx-prev] / [data-smx-next] buttons anywhere inside the element.
  Attributes: data-autoplay="true", data-interval="5000" (ms).
  data-ticker="true": instead of stepping, scroll slowly and nonstop in a loop
  (data-interval = ms to move one card). The cards are copied once so the loop is seamless.
  Auto-scroll pauses on hover, focus and when the tab is hidden, and is off
  for visitors who ask for reduced motion.
*/
if (!customElements.get('smx-carousel')) {
  class SmxCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-smx-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-smx-prev]');
      this.next = this.querySelector('[data-smx-next]');
      this.paused = false;

      if (this.prev) this.prev.addEventListener('click', () => this.go(-1, true));
      if (this.next) this.next.addEventListener('click', () => this.go(1, true));
      this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
      window.addEventListener('resize', () => this.updateButtons());

      ['mouseenter', 'focusin', 'touchstart'].forEach((e) =>
        this.addEventListener(e, () => (this.paused = true), { passive: true })
      );
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const designMode = window.Shopify && window.Shopify.designMode;
      if (this.dataset.ticker === 'true' && this.dataset.autoplay === 'true' && !reduce && !designMode) {
        this.startTicker(parseInt(this.dataset.interval, 10) || 5000);
      } else if (this.dataset.autoplay === 'true' && !reduce) {
        const ms = parseInt(this.dataset.interval, 10) || 5000;
        this.timer = setInterval(() => {
          if (!this.paused && !document.hidden && !(window.Shopify && window.Shopify.designMode)) this.go(1, false);
        }, ms);
      }
      this.updateButtons();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      cancelAnimationFrame(this.raf);
    }

    startTicker(msPerCard) {
      const originals = Array.from(this.track.children);
      if (originals.length < 2) return;
      originals.forEach((el) => {
        const copy = el.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('inert', '');
        copy.removeAttribute('id');
        this.track.appendChild(copy);
      });
      this.ticker = true;
      this.track.style.scrollSnapType = 'none';
      const loop = () => this.track.children[originals.length].offsetLeft - this.track.children[0].offsetLeft;
      let pos = this.track.scrollLeft;
      let last = performance.now();
      const tick = (now) => {
        const dt = Math.min(now - last, 100);
        last = now;
        const width = loop();
        if (this.paused || document.hidden) {
          pos = this.track.scrollLeft;
        } else if (width > 0) {
          pos += (this.step() / msPerCard) * dt;
          if (pos >= width) pos -= width;
          this.track.scrollLeft = pos;
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
      this.loopWidth = loop;
    }

    step() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    atEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }

    go(dir, user) {
      if (this.ticker) {
        const width = this.loopWidth();
        if (dir < 0 && this.track.scrollLeft < this.step()) this.track.scrollLeft += width;
        if (dir > 0 && this.track.scrollLeft >= width) this.track.scrollLeft -= width;
        this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
        return;
      }
      if (dir > 0 && this.atEnd()) {
        if (user) return;
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
    }

    updateButtons() {
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
      this.toggleAttribute('data-scrollable', scrollable);
      if (this.ticker) return;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.atEnd();
    }
  }
  customElements.define('smx-carousel', SmxCarousel);
}
